import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  TixGetAppleWalletPassUrlGQL,
  TixGetGoogleWalletUrlGQL,
  TixGetPolicyByVenueIdGQL
} from '@tix/data-access';
import { POLICY_TYPES } from '@tix/shared/state';
import { TixVenuesService } from '@tix/venues/state';
import {
  TicketDownloadBaseComponent,
  TicketDownloadService
} from '@tix/tickets';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'apps/purchasing-portal/src/environments/environment';
import { AmplitudeService } from '@tix/core/analytics';
import { TixAnalyticsService } from '@tix/shared/analytics';

@Component({
  selector: 'tix-payment-success',
  templateUrl: './payment-success.component.html',
  styleUrls: ['./payment-success.component.scss']
})
export class TixPaymentSuccessPage
  extends TicketDownloadBaseComponent
  implements OnInit
{
  constructor(
    private router: Router,
    private getPolicyByVenueId: TixGetPolicyByVenueIdGQL,
    private venuesService: TixVenuesService,
    snackbar: MatSnackBar,
    ticketDownloadService: TicketDownloadService,
    private getGoogleWalletUrl: TixGetGoogleWalletUrlGQL,
    private getAppleWalletUrl: TixGetAppleWalletPassUrlGQL,
    private amplitudeService: AmplitudeService,
    private tixAnalytics: TixAnalyticsService
  ) {
    super(ticketDownloadService, snackbar);
  }

  venueId: string;
  thankYouMessage: string;
  loading = true;
  downloadAnimation = false;
  walletAnimation = false;
  reservationId: string;
  venue$: any;

  async ngOnInit() {
    this.venueId = this.router.url.split('/')[2].split('?')[0];
    this.venue$ = this.venuesService.getVenueById(this.venueId);
    this.reservationId = this.router.url.split('/')[3].split('?')[0];
    try {
      const policyResponse = await this.getPolicyByVenueId
        .fetch({
          policyType: POLICY_TYPES.THANK_YOU,
          venueId: this.venueId
        })
        .toPromise();
      this.thankYouMessage = policyResponse.data.Policy[0]?.policyText;
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  downloadTickets(reservationId: string) {
    this.amplitudeService.trackEvent('Button Clicked: Download Tickets PDF');
    this.tixAnalytics.captureEvent('download_tickets', {
      venueId: this.venueId,
      reservationId
    });
    this.downloadAnimation = true;
    this.onGetTicketsClicked(reservationId)
      .catch(error => console.error('Ticket Download Error:', error))
      .finally(() => (this.downloadAnimation = false));
  }

  trackButtonClick(eventName: string) {
    this.amplitudeService.trackEvent(`Button Clicked: ${eventName}`);
  }

  async openGoogleWalletUrl(reservationId: string) {
    this.walletAnimation = true;
    this.amplitudeService.trackEvent('Button Clicked: Google Wallet');
    this.tixAnalytics.captureEvent('save_tickets_to_wallet', {
      venueId: this.venueId,
      reservationId,
      walletType: 'google'
    });
    try {
      const googleUrlResponse = await this.getGoogleWalletUrl
        .fetch({
          data: {
            baseURL: window.location.origin + '/check-ticket',
            reservationId: reservationId
          }
        })
        .toPromise();
      const googleWalletUrl = googleUrlResponse.data.getGoogleWalletUrl;
      if (googleWalletUrl) {
        window.open(googleWalletUrl);
      }
    } catch (error) {
      console.error('Google Wallet URL Error:', error);
    } finally {
      this.walletAnimation = false;
    }
  }

  async openAppleWalletUrl(reservationId: string) {
    this.walletAnimation = true;
    this.amplitudeService.trackEvent('Button Clicked: Apple Wallet');
    this.tixAnalytics.captureEvent('save_tickets_to_wallet', {
      venueId: this.venueId,
      reservationId,
      walletType: 'apple'
    });
    try {
      const appleUrlResponse = await this.getAppleWalletUrl
        .fetch({
          data: {
            baseURL: window.location.origin + '/check-ticket',
            reservationId: reservationId
          }
        })
        .toPromise();
      const appleWalletUrl =
        environment.customApiUrl + appleUrlResponse.data.getAppleWalletPassUrl;
      if (appleWalletUrl) {
        window.open(appleWalletUrl);
      }
    } catch (error) {
      console.error('Apple Wallet URL Error:', error);
    } finally {
      this.walletAnimation = false;
    }
  }

  venueRoute(venue: any) {
    if (venue.slug) return [`/${venue.slug}`];
    else return ['/venue', this.venueId];
  }

  shouldShowGoogleWalletButton(): boolean {
    const userAgent = navigator.userAgent.toUpperCase();
    return userAgent.includes('WIN') || /ANDROID/.test(userAgent);
  }

  shouldShowAppleWalletButton(): boolean {
    const userAgent = navigator.userAgent.toUpperCase();
    return userAgent.includes('MAC') || /(IPHONE|IPOD|IPAD)/.test(userAgent);
  }
}
